import type { AccountAppearance } from './auth';
import { WORKBENCH_WIDTH } from '../constants/workspace-layout';
import type { WorkspacePermissionRule } from '../workspace-access';

export const WORKSPACE_GRANTS = [
  'workspace.read',
  'workspace.configure',
  'workspace.layout.read',
  'workspace.layout.write',
  'workspace.member.invite',
  'workspace.member.manage',
  'page.create',
  'page.update',
  'page.archive',
  'component.create',
  'component.edit',
  'component.publish',
  'component.install',
  'record.read',
  'record.write',
  'record.delete',
  'schedule.manage',
  'realtime.subscribe',
  'realtime.publish',
  'tool.execute',
  'agent.run',
  'agent.manage',
  'agent.approve',
  'secret.bind',
] as const;

export type WorkspaceGrant = (typeof WORKSPACE_GRANTS)[number];

export type WorkspaceStatus = 'active' | 'archived' | 'deleting';

export const WORKSPACE_COMPONENT_HEIGHT_MODES = ['default', 'overflow-expand', 'auto'] as const;
export type WorkspaceComponentHeightMode = (typeof WORKSPACE_COMPONENT_HEIGHT_MODES)[number];

/** @deprecated 请改用 AccountAppearance；主题不再属于工作台。 */
export type WorkspaceAppearance = AccountAppearance;

export interface WorkspaceMemberProfile {
  displayName: string;
  email: string;
  avatar: string | null;
}

export interface WorkspaceMemberPreferences {
  agentEntryMode: 'composer' | 'launcher';
  agentWindowPinned: boolean;
  /** 当前成员在此工作台最近打开的主控会话；null 表示新会话空态。 */
  lastMasterSessionId: string | null;
  /** 当前成员在此工作台最近打开的页面。 */
  lastPageId: string | null;
  /** null 表示铺满可用区域。 */
  workbenchWidth: number | null;
  /** null 表示进入时打开上次访问的页面。 */
  workbenchStartPageId: string | null;
  componentAgentEntryVisibility: 'auto' | 'always';
}

export type WorkbenchPreferences = Pick<
  WorkspaceMemberPreferences,
  'workbenchWidth' | 'workbenchStartPageId' | 'componentAgentEntryVisibility'
>;

export const DEFAULT_WORKSPACE_MEMBER_PREFERENCES: WorkspaceMemberPreferences = {
  agentEntryMode: 'composer',
  agentWindowPinned: false,
  lastMasterSessionId: null,
  lastPageId: null,
  workbenchWidth: WORKBENCH_WIDTH.default,
  workbenchStartPageId: null,
  componentAgentEntryVisibility: 'auto',
};

export interface WorkspaceView {
  id: string;
  ownerId: string;
  /** 当前登录用户在此工作台中的角色；系统任务等非用户上下文可能不返回。 */
  memberRole?: string;
  memberGrants?: WorkspaceGrant[];
  createdBy: string;
  effectivePlan: 'free' | 'pro' | 'team';
  billingState: 'active' | 'team_expired_pro' | 'team_expired_free';
  componentInstanceCount: number;
  componentInstanceLimit: number;
  slug: string;
  name: string;
  description: string | null;
  status: WorkspaceStatus;
  archivedAt: string | null;
  currentRevision: number;
  settingsRevision: number;
  createdAt: string;
  updatedAt: string;
}

export interface WorkspaceMemberView {
  id: string;
  workspaceId: string;
  userId: string;
  role: string;
  roleName?: string;
  grants: WorkspaceGrant[];
  roleGrants?: WorkspaceGrant[];
  resourceRules?: WorkspacePermissionRule[];
  permissionOverrides?: WorkspacePermissionRule[];
  readOnly?: boolean;
  status: 'invited' | 'active' | 'disabled';
  invitedBy: string | null;
  invitedAt: string | null;
  acceptedAt: string | null;
  creditLimit: number | null;
  creditUsed: number;
  creditReserved: number;
  preferences: WorkspaceMemberPreferences;
  profile: WorkspaceMemberProfile | null;
}

export interface WorkspaceUsageMetricView {
  used: number;
  limit: number;
  remaining: number;
}

/** 当前工作台的资源额度。资源资产始终计入工作台所有者账户。 */
export interface WorkspaceResourceUsageView {
  scope: 'workspace_owner_account';
  storageBytes: WorkspaceUsageMetricView;
  recordCount: WorkspaceUsageMetricView;
}

export interface WorkspaceCreditBalanceView {
  availableCredits: number;
  reservedCredits: number;
  totalCredits: number;
}

/** 工作台、成员及可由当前 Agent 使用的资源概览。 */
export interface WorkspaceContextView extends WorkspaceView {
  currentMember: WorkspaceMemberView | null;
  members: WorkspaceMemberView[] | null;
  resourceUsage: WorkspaceResourceUsageView | null;
  credits: WorkspaceCreditBalanceView | null;
}

export interface WorkspaceInvitationView {
  workspace: Pick<WorkspaceView, 'id' | 'name' | 'description' | 'ownerId'>;
  member: WorkspaceMemberView;
}

export interface WorkspaceDeletionImpact {
  members: number;
  pages: number;
  installations: number;
  instances: number;
  records: number;
  files: number;
  agents: number;
  sessions: number;
  messages: number;
  memories: number;
  tasks: number;
  extensions: number;
}

export interface WorkspaceDeletionJobView {
  id: string;
  workspaceId: string;
  workspaceName: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  step: string;
  error: string | null;
  impact: WorkspaceDeletionImpact;
  createdAt: string;
  updatedAt: string;
}
